import { Identity } from '../account'
import { Category } from '../transaction/category'
import {
  AWXDepositActivityStatus,
  PaymentMethod,
  TransactionStatus,
  TransactionType,
  VatCode,
} from '../transaction/transaction'
import { GlobalAccountPayload } from '../wallet/global.account'

export type Charter = {
  id: string
  awxConnectedAccountId: string
  name: string
  startDate: string
  endDate: string
  charterFeeCents: number
  apaCents: number
  currencyCode: string
  contactFullName: string
  createdAt: string
  updatedAt: string
  createdBy: string
  status: CharterStatus
  delivery?: string
  reDelivery?: string
  vat?: string
  contactEmail?: string
  contactMobileCountryCode?: string
  contactMobileNumber?: string
  gratuityTransactionId?: string
  signOffType?: SignOffType
  signedPdfAttachmentId?: string
  clientSignature?: string
  clientSignatureType?: SignatureType
  signerPrintedName?: string
  purserSignature?: string
  purserPrintedName?: string
  clientSignatureDate?: string
  purserSignatureDate?: string
}

export enum SignOffType {
  DIGITAL = 'DIGITAL',
  PDF = 'PDF',
}

export enum CharterStatus {
  // the charter is upcoming and not yet started, anyone can submit a transaction (manual or digital)
  UPCOMING = 'UPCOMING',
  // the charter is in progress, anyone can submit a transaction (manual or digital)
  IN_PROGRESS = 'IN_PROGRESS',
  // the charter has been signed off, only the admin/owner can submit a transaction (manual)
  SIGNED_OFF = 'SIGNED_OFF',
  // the charter is completed, no more transactions can be submitted
  COMPLETED = 'COMPLETED',
  // the charter is cancelled, no more transactions can be submitted
  CANCELLED = 'CANCELLED',
}

export type CharterSummary = Omit<Charter, 'createdBy'> & {
  createdBy: Identity
  /**
   * Remaining APA in cents for list/dashboard views.
   * Same idea as charter detail `getCharterStats`: attributed deposits − non-rejected spend − payable VAT
   * (not contracted `apaCents` minus approved-only expenses). May be negative when overspent.
   */
  apaRemainingCents?: number
}

export type CharterForTransactions = Pick<Charter, 'id' | 'name' | 'currencyCode'>

export type CreateCharterRequest = {
  /**
   * The name of the charter
   */
  name: string
  /**
   * The start date (ISO 8601) string of the charter
   */
  startDate: string
  /**
   * The end date (ISO 8601) string of the charter
   */
  endDate: string
  /**
   * The charter fee in cents
   */
  charterFeeCents: number
  /**
   * The APA fee in cents
   */
  apaCents: number
  /**
   * The currency code (e.g. AUD)
   */
  currencyCode: string
  /**
   * The full name of the contact
   */
  contactFullName: string
  /**
   * The delivery location of the charter
   */
  delivery?: string
  /**
   * The re-delivery location of the charter
   */
  reDelivery?: string
  /**
   * The VAT rate (e.g. '10')
   */
  vat?: string
  /**
   * The email of the contact
   */
  contactEmail?: string
  /**
   * The mobile country code of the contact (e.g. +61)
   */
  contactMobileCountryCode?: string
  /**
   * The mobile number of the contact
   */
  contactMobileNumber?: string
}

export type UpdateCharterRequest = Omit<CreateCharterRequest, 'currencyCode'> & {
  status: CharterStatus
  // we want to override currencyCode as being optional on updates
  currencyCode?: string
}

export enum SignatureType {
  /**
   * Printed full name of the signer. Consist in a simple string containing the signer full name
   */
  PRINTED = 'printed',
  /**
   * A base64 encoded image or any other rich string representation of the signer signature
   */
  ELECTRONIC = 'electronic',
}

export type SignOffCharterRequest = {
  // the sign off status when ready to sign off the charter
  status?: CharterStatus.SIGNED_OFF
  // the sign off type is always required
  signOffType: SignOffType
  // singed off pdf charter attachment id, required when signOffType is PDF
  signedPdfAttachmentId?: string
  // client signature, required when signOffType is DIGITAL
  clientSignature?: string
  // signer printed name, required when signOffType is DIGITAL
  signerPrintedName?: string
  // signer signature type, required when signOffType is DIGITAL
  clientSignatureType?: SignatureType
  // always inferred at sign off time
  purserSignature?: string
  // always inferred at sign off time
  purserPrintedName?: string
  clientSignatureDate?: string
  // always inferred at sign off time
  purserSignatureDate?: string
  // the transaction id of the gratuity option selected by the client, Required before signature, can be used
  // with the get transaction endpoint to display the read only info
  gratuityTransactionId?: string
}

export type UpdateCharterLedgerRequest = Omit<
  SignOffCharterRequest,
  'gratuityTransactionId' | 'purserSignature' | 'purserPrintedName' | 'purserSignatureDate' | 'clientSignatureDate'
> & {
  gratuity?: {
    transactionId?: string
    amountCents: number
    label: string
  }
  purserAccountId?: string
  charterCurrencyCode: string
  awxConnectedAccountId: string
}

// do not use the same type of the backend since that can change and we don't want to impact the client
export type UpdateCharterLedgerPayload = {
  // the sign off status when ready to sign off the charter
  status?: CharterStatus.SIGNED_OFF
  // the sign off type is always required
  signOffType: SignOffType
  // singed off pdf charter attachment id, required when signOffType is PDF
  signedPdfAttachmentId?: string
  // client signature, required when signOffType is DIGITAL
  clientSignature?: string
  // client signature type, required when signOffType is DIGITAL
  clientSignatureType?: SignatureType
  // signer printed name, required when signOffType is DIGITAL
  signerPrintedName?: string
  // client signature date, required when signOffType is DIGITAL
  clientSignatureDate?: string
  // the gratuity option selected by the client, Required before signature
  gratuity?: {
    // the amount in cents of the gratuity option selected by the client
    amountCents: number
    // the label of the gratuity option selected by the client (must be from the ledger and not altered)
    label: string
  }
}

// Shared with the search handler Zod schema so the type and allowlist stay in sync
export const CHARTER_SORT_BY = [
  'updatedAt',
  'createdAt',
  'startDate',
  'endDate',
  'name',
  'status',
  'currencyCode',
  'apaCents',
] as const

export type CharterSortBy = (typeof CHARTER_SORT_BY)[number]

export type SearchChartersRequest = {
  /**
   * Text search query for charter name (case-insensitive)
   */
  query?: string
  /**
   * Filter by charter status(es)
   */
  status?: CharterStatus[]
  /**
   * Filter charters with start date >= this date (ISO 8601)
   */
  startDateFrom?: string
  /**
   * Filter charters with start date <= this date (ISO 8601)
   */
  startDateTo?: string
  /**
   * Filter charters with end date >= this date (ISO 8601)
   */
  endDateFrom?: string
  /**
   * Filter charters with end date <= this date (ISO 8601)
   */
  endDateTo?: string
  /**
   * Filter charters with charter fee >= this amount (in cents)
   */
  charterFeeMin?: number
  /**
   * Filter charters with charter fee <= this amount (in cents)
   */
  charterFeeMax?: number
  /**
   * Filter charters with APA fee >= this amount (in cents)
   */
  apaMin?: number
  /**
   * Filter charters with APA fee <= this amount (in cents)
   */
  apaMax?: number
  /**
   * Number of records to return (default: 10, max: 1000)
   */
  take?: number
  /**
   * Number of records to skip for pagination
   */
  skip?: number
  /**
   * Field to sort by (default: startDate)
   */
  sortBy?: CharterSortBy
  /**
   * Sort order (default: desc)
   */
  sortOrder?: 'asc' | 'desc'
  /**
   * Filter charters with currency code
   */
  currencyCode?: string
}

export enum AttachmentStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  FAILED = 'FAILED',
}

export type PrepareCharterAttachmentUploadRequest = {
  name: string
  mimeType: string
  charterId: string
  uploadedBy: string
}

export type PrepareCharterAttachmentUploadResponse = {
  id: string
  uploadUrl: string
  uploadToken: string
}

export type ConfirmCharterAttachmentUploadRequest = {
  uploadToken: string
}

export type CreateCharterAttachmentRequest = {
  name: string
  mimeType: string
  charterId: string
  uploadedBy: string
  storageKey?: string
}

export type CharterAttachment = {
  id: string
  charterId: string
  name: string
  mimeType: string
  uploadedAt: string
  storageKey: string
  uploadedBy: string
}

export type CharterAttachmentPayload = Omit<CharterAttachment, 'uploadedBy' | 'storageKey' | 'charterId'> & {
  url: string
}

export type CharterAttachmentBody = {
  name: string
  mimeType: string
}

export type PreSignedAttachmentUploadBody = {
  filename: string
}

export type CharterTransactions = {
  id: string
  apaAmountCents: bigint
  transactions: {
    amountCents: bigint
    payableVatAmountCents: bigint
    status: TransactionStatus
  }[]
}

/**
 * Batch spend/deposit aggregates for charter list enrichment.
 * Sums are signed as stored; the service applies `calculateApaRemainingCents` on top.
 */
export type CharterApaAggregates = {
  spend: {
    charterId: string
    amountCents: number
    payableVatAmountCents: number
  }[]
  deposits: {
    charterId: string
    amountCents: number
  }[]
}

export type CharterStatsPayload = {
  id: string
  apaSpentCents: number
  apaRemainingCents: number
  payableVatAmountCents: number
  nonReviewedTransactionsCount: number
  apaFunds?: {
    receivedCents: number
    deposits: CharterStatusDeposit[]
  }
}

export type CharterStatusDeposit = {
  id: string
  amountCents: number
  status: AWXDepositActivityStatus
  depositDate: string
}

/**
 * Represents a type that contains all the info required to generate a charter ledger
 */
export type CharterLedger = {
  // charter with picked fields only
  charter: Pick<
    Charter,
    'id' | 'name' | 'startDate' | 'endDate' | 'status' | 'apaCents' | 'charterFeeCents' | 'currencyCode' | 'vat'
  > & {
    apaFundsReceivedCents: number
  }
  // a summary of all the expenses
  summary: CharterLedgerSummary
  // a list of ordered transactions from the newest to the oldest
  transactions: CharterLedgerTransaction[]
  // list of gratuity options for the client to choose from
  gratuity: {
    options: {
      label: string
      amountCents?: number
      // whether the option is selected by the client, this will depend on the transaction id info
      selected: boolean
    }[]
  }
  // the transaction id of the gratuity option selected by the client, Required before signature
  gratuityTransactionId?: string
  // the charter attachment id of the signed pdf, required when signOffType is PDF
  signedPdfAttachmentId?: string
  // the type of sign off used by the client
  signOffType?: SignOffType
  // field missing when ledger not signed yet or manually signed
  signatures: {
    // the client (charterer) signature, required when signOffType is DIGITAL
    charterer?: {
      signatureType?: SignatureType
      signature?: string
      fullName?: string
      date?: string
    }
    // the purser (captain) signature, inferred when status changed to SIGNED_OFF
    purser?: {
      signature?: string
      fullName?: string
      date?: string
    }
  }
  // global account for bank details, optional
  globalAccount?: GlobalAccountPayload
}

export type CharterLedgerSummary = {
  // total spend for each category, including other (uncategorized)
  totalByCategories: {
    [categoryId: string]: {
      totalAmountCents: number
    }
  }
  categories: Omit<Category, 'organizationId'>[]
  // the total payable vat amount for all transactions
  totalPayableVatAmountCents: number
  // what's left in the APA
  balanceCents: number
}

export type CharterLedgerTransaction = {
  // the transaction id
  id: string
  // the date when the transaction occurred
  date: string
  // the transaction status
  status: TransactionStatus
  // the transaction category
  category?: Omit<Category, 'organizationId'>
  // any comment on the transaction
  comments?: string
  // the payment method used for the transaction
  paymentMethod: PaymentMethod
  // the transaction type
  type: TransactionType
  // full name of the person who submitted the transaction
  spenderFullName: string
  // the VAT code applied to the transaction
  vatCode?: VatCode
  // the merchant/vendor of the transaction
  vendor?: string
  /**
   * The VAT rate applied to the transaction (falls back to the charter vat rate when not provided)
   */
  vat?: string
  // the amount in cents of the transaction
  amountCents: number
  // the payable VAT amount in cents for this transaction
  payableVatAmountCents: number
  // the carrying apa balance
  carryingAPABalanceCents: number
  /**
   * Newest ACTIVE transaction attachment id for Sign-Off receipt preview (omit when none).
   */
  firstReceiptAttachmentId?: string
}

export function isClosedCharter(status: CharterStatus) {
  return [CharterStatus.COMPLETED, CharterStatus.CANCELLED].includes(status)
}
